import React, { useEffect, useState, useMemo } from "react";
import Loader from "../../../../../components/Loader";
import { useTranslation } from "react-i18next";

export default function TypeGeneralSurvey({ data, people, setDataGraph, setFormattedHeader, filteredPosts, user }) {
    const [generalSurvey, setGeneralSurvey] = useState();
    const { t, i18n } = useTranslation();
    
    const normalizedFilteredPosts = useMemo(() => {
        if (Array.isArray(filteredPosts)) {
            const personNames = filteredPosts
                .map(entry => entry.personName?.trim().toLowerCase())
                .filter(Boolean);
            return { personNames };
        }
        return { personNames: [] };
    }, [filteredPosts]);

    useEffect(() => {
        if (data && data.length > 0) {
            handleGraphGeneralSurvey(data);
        }
    }, [data, user, filteredPosts, people]);

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    // ---- UTILITAIRES DE RECHERCHE ET DE VALIDATION ----
    const checkNameMatch = (itemName, person) => {
        if (!itemName) return false;
        const p1 = `${person.firstName || ''} ${person.lastName || ''}`.trim().toLowerCase();
        const p2 = `${person.lastName || ''} ${person.firstName || ''}`.trim().toLowerCase();
        const itemLower = itemName.toLowerCase();

        return itemLower === p1 ||
            itemLower === p2 ||
            itemLower.includes(p1) ||
            itemLower.includes(p2) ||
            p1.includes(itemLower) ||
            p2.includes(itemLower);
    };

    const getPeopleByNumberMap = (peopleFlat) => {
        const map = new Map();
        if(!peopleFlat) return map;
        peopleFlat.forEach(p => {
            [p.phone?.work, p.phone?.mobile].filter(Boolean).forEach(num => {
                if (!map.has(num)) map.set(num, []);
                map.get(num).push(p);
            });
        });
        return map;
    };

    const handleGraphGeneralSurvey = (data) => {
        const canUseCallerId = user && user.holdingId === 33;
        const personNames = normalizedFilteredPosts.personNames;
        
        const peopleFlat = people ? people.flat() : [];
        const peopleByNumber = getPeopleByNumberMap(peopleFlat);
        const knownNumbers = new Set(peopleByNumber.keys());

        let Outgoing = {
            total: 0,
            durationTotal: 0,
            averageduration: 0,
            response: 0,
            averageresponse: 0,
            directcalls: 0, // Ajout pour éviter les erreurs
            callsforwarded: 0 // Ajout pour éviter les erreurs
        };
        let Incoming = {
            total: 0,
            durationTotal: 0,
            averageduration: 0,
            response: 0,
            averageresponse: 0,
            directcalls: 0,
            callsforwarded: 0,
            lostcalls: 0
        };

        data.forEach(item => {
            // =========================================================
            // BOUCLIER DE SÉCURITÉ : UNIQUEMENT POUR HOLDING 33
            // =========================================================
            if (canUseCallerId) {
                const isOutgoing = item.direction === "ORIGINATING";
                const isIncoming = item.direction === "TERMINATING";
                let isCallValidForUser = false;

                if (isOutgoing) {
                    const workOrMobile = item.callingnumber;
                    if (knownNumbers.has(workOrMobile)) {
                        const itemName = (item.callinglineid || "").trim();
                        const persons = peopleByNumber.get(workOrMobile) || [];

                        let matchedName = "";
                        if (itemName) {
                            const bestMatch = persons.find(p => checkNameMatch(itemName, p));
                            matchedName = bestMatch ? `${bestMatch.firstName} ${bestMatch.lastName}` : itemName;
                        } else {
                            matchedName = persons.length > 0 ? `${persons[0].firstName} ${persons[0].lastName}` : "";
                        }

                        if (personNames && personNames.length > 0) {
                            const nameToVerify = itemName ? itemName.toLowerCase() : matchedName.toLowerCase();
                            isCallValidForUser = personNames.some(filterName =>
                                nameToVerify.includes(filterName) || filterName.includes(nameToVerify)
                            );
                        } else {
                            isCallValidForUser = true; // S'il n'y a pas de filtre par nom, on garde tout
                        }
                    }
                } else if (isIncoming) {
                    const calledNum = item.callednumber;
                    if (knownNumbers.has(calledNum)) {
                        const itemName = (item.calledlineid || "").trim();
                        const persons = peopleByNumber.get(calledNum) || [];

                        let matchedName = "";
                        if (itemName) {
                            const bestMatch = persons.find(p => checkNameMatch(itemName, p));
                            matchedName = bestMatch ? `${bestMatch.firstName} ${bestMatch.lastName}` : itemName;
                        } else {
                            matchedName = persons.length > 0 ? `${persons[0].firstName} ${persons[0].lastName}` : "";
                        }

                        if (personNames && personNames.length > 0) {
                            const nameToVerify = itemName ? itemName.toLowerCase() : matchedName.toLowerCase();
                            isCallValidForUser = personNames.some(filterName =>
                                nameToVerify.includes(filterName) || filterName.includes(nameToVerify)
                            );
                        } else {
                            isCallValidForUser = true;
                        }
                    }
                }

                // Si le client 33 a un filtre et que l'appel ne correspond pas, on skip
                if (!isCallValidForUser && personNames.length > 0) return;
            }
            // =========================================================
            // FIN DU BOUCLIER HOLDING 33
            // =========================================================

            // Suite du traitement classique (pour tout le monde, et pour les appels 33 qui ont survécu au bouclier)
            if (item.direction === "TERMINATING") {
                Incoming.total += 1;
                Incoming.durationTotal += parseInt(item.duration) || 0;
                if (item.answerindicator === "Yes") {
                    Incoming.response += 1;
                    Incoming.averageresponse += parseInt(item.ringduration) || 0;
                }
            } else if (item.direction === "ORIGINATING") {
                Outgoing.total += 1;
                Outgoing.durationTotal += parseInt(item.duration) || 0;
                if (item.answerindicator === "Yes") {
                    Outgoing.response += 1;
                    Outgoing.averageresponse += parseInt(item.ringduration) || 0;
                }
                if (item.calloutcome === 'Success') {
                    if (item.transferrelatedcallid) {
                        Outgoing.callsforwarded += 1;
                    } else {
                        Outgoing.directcalls += 1;
                    }
                } else if (item.calloutcome === 'Failure') {
                    Incoming.lostcalls += 1;
                }
            }
        });

        Outgoing.averageduration = Outgoing.total > 0 ? Outgoing.durationTotal / Outgoing.total : 0;
        Outgoing.averageresponse = Outgoing.response > 0 ? Outgoing.averageresponse / Outgoing.response : 0;

        Incoming.averageduration = Incoming.total > 0 ? Incoming.durationTotal / Incoming.total : 0;
        Incoming.averageresponse = Incoming.response > 0 ? Incoming.averageresponse / Incoming.response : 0;

        Outgoing.durationTotal = formatDuration(Outgoing.durationTotal);
        Outgoing.averageduration = formatDuration(Outgoing.averageduration);
        Outgoing.averageresponse = formatDuration(Outgoing.averageresponse);

        Incoming.durationTotal = formatDuration(Incoming.durationTotal);
        Incoming.averageduration = formatDuration(Incoming.averageduration);
        Incoming.averageresponse = formatDuration(Incoming.averageresponse);

        setGeneralSurvey({ Outgoing, Incoming });
        const formattedData = formatForExcel({ Outgoing, Incoming });
        setDataGraph(formattedData);
    }

    const formatForExcel = (data) => {
        // En-têtes des colonnes
        const headers = ["", "Sortants", "Entrants"];

        const keysMapping = [
            { key: "total", label: "Nombre d'appels" },
            { key: "durationTotal", label: "Durée totale d'appels" },
            { key: "averageduration", label: "Durée moyenne d'appels" },
            { key: "response", label: "Nombre d'appels répondu" },
            { key: "averageresponse", label: "Durée moyenne pour répondre" },
            { key: "directcalls", label: "Nombre d'appels directs" },
            { key: "callsforwarded", label: "Nombre d'appels transférés" },
            { key: "lostcalls", label: "Nombre d'appels perdus" }
        ];

        // Format des données pour Excel : première ligne = headers
        const excelData = [headers];
    
        // Parcourir chaque clé et créer les lignes associées
        keysMapping.forEach(({ key, label }) => {
            const row = [
                label, 
                data.Outgoing[key] !== undefined ? data.Outgoing[key] : null, // Valeur pour Sortants
                data.Incoming[key] !== undefined ? data.Incoming[key] : null  // Valeur pour Entrants
            ];
            excelData.push(row); // Ajouter la ligne à excelData
        });

        setFormattedHeader(true);

        return excelData;
    };

    if (generalSurvey && Object.keys(generalSurvey).length === 0) {
        return (
            <div>{t("Aucune donnée disponible")}</div>
        );
    }

    return (
        <div className="generalSurvey">
            {generalSurvey ? (
                <div>
                    <table className="generalSurvey-sortants">
                        <thead className="sortants-head">
                            <tr>
                                <th colSpan={2}>{t("Sortants")}</th>
                            </tr>
                        </thead>
                        <tbody className="sortants-body">
                            <tr>
                                <td title={t("Nombre d'appels")}>{t("Nombre d'appels")}</td>
                                <td>{generalSurvey.Outgoing.total}</td>
                            </tr>
                            <tr>
                                <td title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</td>
                                <td>{generalSurvey.Outgoing.durationTotal}</td>
                            </tr>
                            <tr>
                                <td title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</td>
                                <td>{generalSurvey.Outgoing.averageduration}</td>
                            </tr>
                            <tr>
                                <td title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</td>
                                <td>{generalSurvey.Outgoing.response}</td>
                            </tr>
                            <tr>
                                <td title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</td>
                                <td>{generalSurvey.Outgoing.averageresponse}</td>
                            </tr>
                        </tbody>
                    </table>
                    <table className="generalSurvey-entrants">
                        <thead className="entrants">
                            <tr>
                                <th colSpan={2}>{t("Entrants")}</th>
                            </tr>
                        </thead>
                        <tbody className="sortants-body">
                            <tr>
                                <td title={t("Nombre d'appels")}>{t("Nombre d'appels")}</td>
                                <td>{generalSurvey.Incoming.total}</td>
                            </tr>
                            <tr>
                                <td title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</td>
                                <td>{generalSurvey.Incoming.durationTotal}</td>
                            </tr>
                            <tr>
                                <td title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</td>
                                <td>{generalSurvey.Incoming.averageduration}</td>
                            </tr>
                            <tr>
                                <td  title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</td>
                                <td>{generalSurvey.Incoming.response}</td>
                            </tr>
                            <tr>
                                <td title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</td>
                                <td>{generalSurvey.Incoming.averageresponse}</td>
                            </tr>
                            <tr>
                                <td title={t("Nombre d'appels directs")}>{t("Nombre d'appels directs")}</td>
                                <td>{generalSurvey.Incoming.directcalls}</td>
                            </tr>
                            <tr>
                                <td title={t("Nombre d'appels transférés")}>{t("Nombre d'appels transférés")}</td>
                                <td>{generalSurvey.Incoming.callsforwarded}</td>
                            </tr>
                            <tr>
                                <td title={t("Nombre d'appels perdus")}>{t("Nombre d'appels perdus")}</td>
                                <td>{generalSurvey.Incoming.lostcalls}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <Loader/>
            )}
        </div>
    );
}